import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, Loader2, ChevronDown, ChevronUp, CheckCircle, XCircle } from 'lucide-react';
import { format, addDays } from 'date-fns';

import { BACKEND_URL, API } from '../utils/apiConfig';

const getTimeCategory = (hour) => {
  if (hour >= 6 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 18) return 'afternoon';
  if (hour >= 18 && hour < 22) return 'evening';
  return 'latenight';
};

const CATEGORIES = {
  morning: { label: '🌅 Morning', subtitle: '6AM - 12PM', color: 'bg-amber-50 border-amber-200' },
  afternoon: { label: '☀️ Afternoon', subtitle: '12PM - 6PM', color: 'bg-sky-50 border-sky-200' },
  evening: { label: '🌆 Evening', subtitle: '6PM - 10PM', color: 'bg-orange-50 border-orange-200' },
  latenight: { label: '🌙 Late Night', subtitle: '10PM - 2AM', color: 'bg-indigo-50 border-indigo-200' }
};

// Date quick options
const getDateOptions = () => {
  const today = new Date();
  const tomorrow = addDays(today, 1);
  const dayAfter = addDays(today, 2);
  
  return {
    today: { label: 'Today', value: format(today, 'yyyy-MM-dd'), day: format(today, 'EEE') },
    tomorrow: { label: 'Tomorrow', value: format(tomorrow, 'yyyy-MM-dd'), day: format(tomorrow, 'EEE') },
    dayAfter: { label: format(dayAfter, 'EEE'), value: format(dayAfter, 'yyyy-MM-dd'), day: format(dayAfter, 'dd MMM') }
  };
};

export default function SlotFinderPage() {
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame, loading: masterLoading } = useMasterData();
  const [selectedGame, setSelectedGame] = useState('');
  const [selectedVenue, setSelectedVenue] = useState('');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [dateMode, setDateMode] = useState('today'); // today, tomorrow, dayAfter, custom
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courtsData, setCourtsData] = useState({}); // { court: { slots, availableCount, bookedCount } }
  const [expandedCourts, setExpandedCourts] = useState([]); // Courts that are expanded
  const [showAllCourts, setShowAllCourts] = useState(false);

  const dateOptions = getDateOptions();
  const VENUES = getVenueNames();

  useEffect(() => {
    if (selectedGame && selectedVenue && selectedDate) {
      fetchAllCourtsAvailability();
    }
  }, [selectedGame, selectedVenue, selectedDate]);

  const fetchAllCourtsAvailability = async () => {
    if (!selectedGame || !selectedVenue) return;
    
    setLoading(true);
    const courts = getCourtsForVenueGame(selectedVenue, selectedGame);
    const allCourtsData = {};

    try {
      // Fetch availability for all courts in parallel
      const promises = courts.map(court => 
        axios.get(`${API}/slot-finder`, {
          params: {
            game: selectedGame,
            venue: selectedVenue,
            court: court,
            date: selectedDate
          }
        }).then(response => ({ court, data: response.data }))
      );

      const results = await Promise.all(promises);

      results.forEach(({ court, data }) => {
        const grouped = {
          morning: [],
          afternoon: [],
          evening: [],
          latenight: []
        };

        let availableCount = 0;
        let bookedCount = 0;
        let blockedCount = 0;

        data.slots.forEach(slot => {
          const category = getTimeCategory(slot.hour);
          grouped[category].push(slot);
          if (slot.available) availableCount++;
          else if (slot.is_blocked) blockedCount++;
          else bookedCount++;
        });

        allCourtsData[court] = {
          slots: grouped,
          availableCount,
          bookedCount,
          blockedCount,
          totalSlots: data.slots.length
        };
      });

      setCourtsData(allCourtsData);

      // Auto-expand first court with availability
      const firstAvailableCourt = courts.find(court => allCourtsData[court]?.availableCount > 0);
      if (firstAvailableCourt) {
        setExpandedCourts([firstAvailableCourt]);
      } else if (courts.length > 0) {
        setExpandedCourts([courts[0]]);
      }

    } catch (error) {
      console.error('Failed to fetch slots:', error);
      toast.error('Failed to load available slots');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (mode) => {
    setDateMode(mode);
    if (mode === 'custom') {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
      setSelectedDate(dateOptions[mode].value);
    }
  };

  const handleGameChange = (value) => {
    setSelectedGame(value);
    setCourtsData({});
    setExpandedCourts([]);
  };

  const handleVenueChange = (value) => {
    setSelectedVenue(value);
    setSelectedGame(''); // Reset game when venue changes
    setCourtsData({});
    setExpandedCourts([]);
  };

  const toggleCourtExpand = (court) => {
    if (expandedCourts.includes(court)) {
      setExpandedCourts(expandedCourts.filter(c => c !== court));
    } else {
      setExpandedCourts([...expandedCourts, court]);
    }
  };

  const courts = selectedVenue && selectedGame ? getCourtsForVenueGame(selectedVenue, selectedGame) : [];
  
  // Sort courts: those with bookings first, then by name
  const sortedCourts = [...courts].sort((a, b) => {
    const aBooked = courtsData[a]?.bookedCount || 0;
    const bBooked = courtsData[b]?.bookedCount || 0;
    // Courts with bookings first
    if (bBooked !== aBooked) return bBooked - aBooked;
    // Then alphabetically
    return a.localeCompare(b, undefined, { numeric: true });
  });
  
  // Show all courts by default - no more hiding
  const displayedCourts = sortedCourts;
  const hasMoreCourts = false; // Disable "show more" since we show all

  const getTotalStats = () => {
    let available = 0;
    let booked = 0;
    let blocked = 0;
    Object.values(courtsData).forEach(data => {
      available += data.availableCount;
      booked += data.bookedCount;
      blocked += data.blockedCount || 0;
    });
    return { available, booked, blocked };
  };

  const stats = getTotalStats();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="slot-finder-page">
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
            Slot Finder
          </h1>
          <p className="text-slate-600 mt-1">Find available time slots across all courts</p>
        </div>

        {/* Filters Card */}
        <Card className="p-6 border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Venue Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Venue <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedVenue} onValueChange={handleVenueChange}>
                <SelectTrigger data-testid="venue-select" className="h-12">
                  <SelectValue placeholder="Select venue" />
                </SelectTrigger>
                <SelectContent>
                  {VENUES.map((venue) => (
                    <SelectItem key={venue} value={venue}>{venue}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Game Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Game <span className="text-red-500">*</span>
              </Label>
              <Select value={selectedGame} onValueChange={handleGameChange} disabled={!selectedVenue}>
                <SelectTrigger data-testid="game-select" className="h-12">
                  <SelectValue placeholder={selectedVenue ? "Select game" : "Select venue first"} />
                </SelectTrigger>
                <SelectContent>
                  {getGamesForVenue(selectedVenue).map((game) => (
                    <SelectItem key={game} value={game}>{game}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">
                Date <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={dateMode === 'today' ? 'default' : 'outline'}
                  className={`flex-1 h-12 ${dateMode === 'today' ? 'bg-primary text-white' : ''}`}
                  onClick={() => handleDateSelect('today')}
                  data-testid="date-today-btn"
                >
                  <div className="text-center">
                    <div className="text-sm font-semibold">Today</div>
                    <div className="text-xs opacity-80">{dateOptions.today.day}</div>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={dateMode === 'tomorrow' ? 'default' : 'outline'}
                  className={`flex-1 h-12 ${dateMode === 'tomorrow' ? 'bg-primary text-white' : ''}`}
                  onClick={() => handleDateSelect('tomorrow')}
                  data-testid="date-tomorrow-btn"
                >
                  <div className="text-center">
                    <div className="text-sm font-semibold">Tomorrow</div>
                    <div className="text-xs opacity-80">{dateOptions.tomorrow.day}</div>
                  </div>
                </Button>
                <Button
                  type="button"
                  variant={dateMode === 'custom' ? 'default' : 'outline'}
                  className={`flex-1 h-12 ${dateMode === 'custom' ? 'bg-primary text-white' : ''}`}
                  onClick={() => handleDateSelect('custom')}
                  data-testid="date-custom-btn"
                >
                  <div className="text-center">
                    <Calendar className="w-4 h-4 mx-auto" />
                    <div className="text-xs">Custom</div>
                  </div>
                </Button>
              </div>
              {showCustomDate && (
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="h-12 mt-2"
                  data-testid="date-input"
                />
              )}
            </div>
          </div>

          {/* Summary Banner */}
          {selectedGame && selectedVenue && Object.keys(courtsData).length > 0 && (
            <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-700" />
                  <span className="font-medium text-green-800">
                    {selectedGame} at {selectedVenue} • {format(new Date(selectedDate), 'EEE, MMM dd')}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span className="font-semibold text-green-700">{stats.available} available</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <span className="font-semibold text-red-600">{stats.booked} booked</span>
                  </div>
                  {stats.blocked > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="text-amber-500">🚫</span>
                      <span className="font-semibold text-amber-600">{stats.blocked} blocked</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Loading State */}
        {loading && (
          <Card className="p-12 text-center border-slate-200">
            <Loader2 className="w-12 h-12 mx-auto mb-3 text-primary animate-spin" />
            <p className="text-slate-600">Finding available slots across all courts...</p>
          </Card>
        )}

        {/* Courts List */}
        {!loading && selectedGame && selectedVenue && Object.keys(courtsData).length > 0 && (
          <div className="space-y-4">
            {displayedCourts.map((court, index) => {
              const courtData = courtsData[court];
              if (!courtData) return null;

              const isExpanded = expandedCourts.includes(court);
              const hasAvailability = courtData.availableCount > 0;

              return (
                <Card 
                  key={court} 
                  className={`border-2 overflow-hidden transition-all ${
                    hasAvailability ? 'border-green-200' : 'border-slate-200'
                  }`}
                  data-testid={`court-card-${index + 1}`}
                >
                  {/* Court Header - Always Visible */}
                  <div 
                    className={`p-4 cursor-pointer transition-colors ${
                      hasAvailability 
                        ? 'bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100' 
                        : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                    onClick={() => toggleCourtExpand(court)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${
                          hasAvailability ? 'bg-green-500 text-white' : 'bg-slate-400 text-white'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <h3 className="font-heading font-semibold text-lg text-slate-900">{court}</h3>
                          <div className="flex items-center gap-3 text-sm">
                            <span className={`font-medium ${hasAvailability ? 'text-green-600' : 'text-slate-500'}`}>
                              {courtData.availableCount} available
                            </span>
                            <span className="text-slate-400">•</span>
                            <span className="text-red-500">{courtData.bookedCount} booked</span>
                            {courtData.blockedCount > 0 && (
                              <>
                                <span className="text-slate-400">•</span>
                                <span className="text-amber-600">🚫 {courtData.blockedCount} blocked</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {hasAvailability && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            Available
                          </span>
                        )}
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Court Slots - Expandable */}
                  {isExpanded && (
                    <div className="p-4 border-t border-slate-200 bg-white">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.entries(CATEGORIES).map(([key, category]) => {
                          const slots = courtData.slots[key];
                          const availableInCategory = slots.filter(s => s.available).length;

                          return (
                            <div key={key} className={`p-3 rounded-lg border ${category.color}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-sm">{category.label}</span>
                                <span className="text-xs text-slate-500">{availableInCategory}/{slots.length}</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {slots.length === 0 ? (
                                  <p className="text-xs text-slate-400 italic">No slots</p>
                                ) : (
                                  slots.map((slot, idx) => (
                                    <div
                                      key={idx}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                                        slot.available
                                          ? 'bg-green-100 text-green-800 border border-green-300'
                                          : slot.is_blocked
                                          ? 'bg-amber-100 text-amber-700 border border-amber-300'
                                          : 'bg-red-50 text-red-600 border border-red-200 opacity-60'
                                      }`}
                                      title={
                                        slot.available 
                                          ? 'Available' 
                                          : slot.is_blocked 
                                          ? `🚫 Blocked: ${slot.block_info?.reason || 'Unknown'}${slot.block_info?.notes ? ` - ${slot.block_info.notes}` : ''}`
                                          : `Booked by ${slot.booking_info?.player_name || 'Someone'}`
                                      }
                                    >
                                      {slot.is_blocked && <span className="mr-1">🚫</span>}
                                      {slot.start_time}
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}

            {/* Show More Courts Button */}
            {hasMoreCourts && (
              <div className="text-center">
                <Button
                  variant="outline"
                  onClick={() => setShowAllCourts(!showAllCourts)}
                  className="px-6"
                  data-testid="show-more-courts-btn"
                >
                  {showAllCourts ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-2" />
                      Show Less Courts
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-2" />
                      Show {courts.length - 3} More Courts
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!loading && (!selectedGame || !selectedVenue) && (
          <Card className="p-12 text-center border-slate-200">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-slate-400" />
            <p className="text-slate-500 mb-2">Select venue and game to see available slots</p>
            <p className="text-sm text-slate-400">
              {!selectedVenue && "Start by selecting a venue"}
              {selectedVenue && !selectedGame && "Now select a game"}
            </p>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
